import React, { useState } from 'react';
import { AopLink, Card, InternalCard, Pill, RiskPill, StatCard } from '../ui';
import { approvalStats, governancePolicy } from '../copData';
import { useCop } from '../CopApp';

const TIER_TONE = { green: 'green', blue: 'blue', amber: 'amber', red: 'red' };

function StageChip({ apr }) {
	if (apr.state !== 'submitted') return null;
	const twoStage = (apr.approval_stages || []).includes('client');
	if (apr.stage === 'client') return <Pill tone="cyan">client review</Pill>;
	return <Pill tone="dim">internal review{twoStage ? ' · 1 of 2' : ''}</Pill>;
}

function clientApproverText(apr) {
	if (apr.stage === 'client') return 'Awaiting your approval';
	if (apr.state === 'approved') return 'Approved';
	if (apr.state === 'rejected') return 'Rejected';
	if (apr.state === 'needs_changes') return 'Changes requested';
	return 'Reviewed proposal';
}

function ApprovalCard({ apr, onDecide, onEdit, isInternal }) {
	const [expanded, setExpanded] = useState(apr.expanded);
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(apr.justification);
	const decided = apr.state !== 'submitted';
	const atInternal = isInternal && !decided && apr.stage === 'internal';
	const advances = !decided && apr.stage === 'internal' && (apr.approval_stages || []).includes('client');

	const saveEdit = () => {
		onEdit(apr.approval_request_id, draft.trim());
		setEditing(false);
	};

	return (
		<div className={`cop-apr-card${decided ? ' decided' : ''}`}>
			<div className="cop-apr-head">
				<div className="cop-apr-headline">
					<span className="cop-apr-action">{apr.action_label}</span>
					<RiskPill level={apr.risk_level} />
					{isInternal ? <StageChip apr={apr} /> : apr.stage === 'client' && <Pill tone="cyan">your review</Pill>}
				</div>
				<Pill tone={apr.impact_chip.startsWith('+') ? 'green' : 'dim'}>{apr.impact_chip}</Pill>
			</div>
			{isInternal && <div className="cop-apr-agent">⬡ {apr.agent} · {apr.model}</div>}

			{editing ? (
				<div style={{ margin: '9px 0 4px' }}>
					<textarea
						className="cop-input"
						style={{ width: '100%', minHeight: 64, resize: 'vertical', fontSize: 13 }}
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
					/>
					<div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
						<button className="cop-btn small primary" onClick={saveEdit}>Save Edit</button>
						<button className="cop-btn small" onClick={() => { setDraft(apr.justification); setEditing(false); }}>Cancel</button>
					</div>
				</div>
			) : (
				<div className="cop-apr-just">
					{apr.justification}
					{apr.edited && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-dim)' }}>(edited)</span>}
				</div>
			)}
			<div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
				{isInternal ? `${apr.approvers} · SLA: ${apr.sla}` : clientApproverText(apr)}
				{decided && (
					<span style={{ marginLeft: 10 }}>
						<Pill tone={apr.state === 'approved' ? 'green' : apr.state === 'rejected' ? 'red' : 'amber'}>{apr.state.replace('_', ' ')}</Pill>
					</span>
				)}
			</div>

			{expanded && (
				<>
					<div className="cop-evidence">
						<div>
							<h5>Evidence summary</h5>
							<ul>
								{apr.evidence.map((e) => (
									<li key={e}>{e}</li>
								))}
							</ul>
						</div>
						<div>
							<h5>Impact breakdown</h5>
							<div className="cop-impact-row">
								<span>Projected Uplift</span>
								<span className="v tone-green">{apr.impact.projected_uplift}</span>
							</div>
							<div className="cop-impact-row">
								<span>Incremental Revenue</span>
								<span className="v">{apr.impact.incremental_revenue}</span>
							</div>
							<div className="cop-impact-row">
								<span>Margin Impact</span>
								<span className={`v ${apr.impact.margin_impact.startsWith('-') ? 'tone-red' : ''}`}>{apr.impact.margin_impact}</span>
							</div>
						</div>
					</div>
					<div className="cop-linked">
						Linked assets:
						{apr.linked.map((l) => (
							<a key={l}>{l} ↗</a>
						))}
						{isInternal && <span style={{ marginLeft: 'auto' }}>💬 Also sent to #campaign-approvals</span>}
					</div>
				</>
			)}

			<div className="cop-apr-actions">
				<span className="cop-apr-rollback">⌁ Rollback plan attached</span>
				<span style={{ flex: 1 }} />
				<button className="cop-link" onClick={() => setExpanded((e) => !e)}>
					{expanded ? 'Hide Evidence' : 'View Evidence →'}
				</button>
				{atInternal && !editing && (
					<button className="cop-btn small" onClick={() => setEditing(true)}>
						Edit
					</button>
				)}
				<button className="cop-btn small" disabled={decided || editing} onClick={() => onDecide(apr.approval_request_id, 'needs_changes')}>
					Request Changes
				</button>
				<button className="cop-btn small danger" disabled={decided || editing} onClick={() => onDecide(apr.approval_request_id, 'rejected')}>
					Reject
				</button>
				<button className="cop-btn small primary" disabled={decided || editing} onClick={() => onDecide(apr.approval_request_id, 'approved')}>
					{advances ? 'Approve → Client' : 'Approve'}
				</button>
			</div>
		</div>
	);
}

export default function ApprovalQueue() {
	const { approvals, decideApproval, editApproval, pendingCount, isInternal } = useCop();
	const [risk, setRisk] = useState('all');
	const highRisk = approvals.filter((a) => a.state === 'submitted' && (a.risk_level === 'high' || a.risk_level === 'critical')).length;
	// Client View shows only actions in (or decided at) the client stage — the
	// same list the account sees in Client Reporting's Approvals section.
	const visible = approvals.filter(
		(a) => isInternal || a.stage === 'client' || (a.state !== 'submitted' && (a.approval_stages || []).includes('client'))
	);
	const filtered = visible.filter((a) => risk === 'all' || a.risk_level === risk);

	return (
		<main className="cop-page">
			<div>
				<h1 className="cop-page-title">Approval Queue</h1>
				<div className="cop-page-sub">
					{isInternal ? (
						<>
							{pendingCount} actions awaiting review · <span className="hot">{highRisk} high-risk</span>
						</>
					) : (
						<>{pendingCount} actions awaiting your approval</>
					)}
				</div>
			</div>

			{isInternal && (
				<div className="cop-stats-row">
					<StatCard label="Pending Approvals" value={String(pendingCount)} trend={[8, 9, 11, 10, 12, 13, 12, pendingCount + 8]} tone="amber" />
					<StatCard label="Approved Today" value={String(approvalStats.approved_today)} trend={[12, 15, 14, 18, 20, 24, 26, 28]} tone="green" />
					<StatCard label="Avg. Time to Decision" value={`${approvalStats.avg_decision_hrs} hrs`} trend={[8, 7, 6.5, 6, 5.4, 5, 4.6, 4.2]} tone="blue" />
					<StatCard label="Auto-Approved (Low-Risk)" value={String(approvalStats.auto_approved)} trend={[20, 24, 28, 31, 36, 40, 44, 47]} tone="purple" />
				</div>
			)}

			<div className="cop-filter-row">
				{['all', 'low', 'medium', 'high', 'critical'].map((r) => (
					<button
						key={r}
						className={`cop-filter-chip${risk === r ? ' active' : ''}`}
						onClick={() => setRisk(r)}
					>
						{r}
					</button>
				))}
			</div>

			<div className="cop-apr-layout" style={isInternal ? undefined : { gridTemplateColumns: '1fr' }}>
				<div>
					{filtered.length === 0 && (
						<Card>
							<div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 20 }}>
								{isInternal ? 'No actions at this risk level.' : 'Nothing awaiting your approval.'}
							</div>
						</Card>
					)}
					{filtered.map((apr) => (
						<ApprovalCard key={apr.approval_request_id} apr={apr} onDecide={decideApproval} onEdit={editApproval} isInternal={isInternal} />
					))}
				</div>

				{isInternal && (
					<InternalCard title="Governance" icon="✓" right="Active Approval Policy" className="cop-gov-rail raise">
						<div style={{ padding: '8px 12px', borderRadius: 9, background: 'var(--inset)', border: '1px solid var(--border)', fontSize: 12.5, marginBottom: 10 }}>
							🏢 {governancePolicy.account}
						</div>
						<div style={{ fontSize: 11, color: 'var(--text-sec)', marginBottom: 4, lineHeight: 1.55 }}>
							{governancePolicy.flow}. Internal reviewers can edit proposals before the client sees them; every edit is audited.
						</div>
						<div className="cop-nav-label" style={{ padding: '4px 0' }}>Risk tiering</div>
						{governancePolicy.tiers.map((t) => (
							<div className="tier-row" key={t.tier}>
								<span>{t.tier}</span>
								<Pill tone={TIER_TONE[t.tone]}>{t.rule}</Pill>
							</div>
						))}
						<div className="cop-nav-label" style={{ padding: '14px 0 4px' }}>Approval SLA</div>
						<div className="cop-gov-note">
							{governancePolicy.sla.map((s) => (
								<div key={s}>{s}</div>
							))}
							<div style={{ marginTop: 6 }}>{governancePolicy.sla_note}</div>
						</div>
						<div className="cop-nav-label" style={{ padding: '14px 0 4px' }}>Escalation</div>
						<div className="cop-gov-note">
							Auto-escalates to <b style={{ color: 'var(--pink)' }}>Head of Growth</b> if no response within the SLA window.
						</div>
						<div className="cop-nav-label" style={{ padding: '14px 0 4px' }}>Client stage</div>
						<div className="cop-gov-note">{governancePolicy.client_surface}</div>
						<div className="cop-aop-links">
							<AopLink view="pending-actions">AOP pending actions</AopLink>
							<AopLink view="audit-log">AOP audit</AopLink>
						</div>
						<div className="cop-gov-foot">{governancePolicy.version}</div>
					</InternalCard>
				)}
			</div>
		</main>
	);
}
